/**
 * <cart-reserve-timer> — countdown band for the cart drawer.
 *
 * The start time is the single source of truth and lives in three places, read in this order:
 *   1. a module-level variable — survives every re-render / re-creation of the element on the page
 *      (cart updates, theme-editor reloads), even where web storage is blocked (e.g. Safari inside
 *      the theme editor's iframe);
 *   2. sessionStorage — survives navigation between pages in the same tab;
 *   3. a first-party cookie — fallback for browsers that block sessionStorage.
 * The element is only rendered while the bag has items. When the bag empties the server HTML no
 * longer contains it, the morph removes it, and disconnectedCallback clears the stored start so the
 * next bag starts fresh. The element also carries data-skip-subtree-update so the theme's DOM morph
 * never writes the server's static "15:00" over the running digits.
 */
const STORAGE_KEY = 'cart-reserve-start';
const COOKIE_KEY = 'cart_reserve_start';

/** @type {number} */
let memoryStart = 0;

function readStart() {
  if (memoryStart) return memoryStart;
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    if (value && Number(value) > 0) return (memoryStart = Number(value));
  } catch {
    /* storage blocked */
  }
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_KEY}=(\\d+)`));
    if (match && Number(match[1]) > 0) return (memoryStart = Number(match[1]));
  } catch {
    /* cookies blocked */
  }
  return 0;
}

/** @param {number} now */
function writeStart(now) {
  memoryStart = now;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    /* storage blocked */
  }
  try {
    document.cookie = `${COOKIE_KEY}=${now}; path=/; max-age=86400; SameSite=Lax`;
  } catch {
    /* cookies blocked */
  }
}

function clearStart() {
  memoryStart = 0;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

class CartReserveTimer extends HTMLElement {
  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    if (!readStart()) writeStart(Date.now());
    this.#render();
    if (this.#interval) window.clearInterval(this.#interval);
    this.#interval = window.setInterval(() => this.#render(), 1000);
  }

  disconnectedCallback() {
    if (this.#interval) window.clearInterval(this.#interval);
    this.#interval = undefined;
    // Removed because the bag emptied (not a page unload): forget the start time. The check runs
    // on the next tick so a morph that detaches and re-attaches the element never clears it.
    window.setTimeout(() => {
      if (!this.isConnected && document.querySelector('.cart-drawer--empty')) clearStart();
    }, 0);
  }

  get #durationMs() {
    const minutes = Number(this.dataset.minutes) || 15;
    return minutes * 60 * 1000;
  }

  #render() {
    const target = this.querySelector('[ref="countdown"]');
    if (!target) return;

    let remaining = this.#durationMs - (Date.now() - readStart());
    if (remaining <= 0) {
      // Window over: start a fresh one rather than sitting on 00:00.
      writeStart(Date.now());
      remaining = this.#durationMs;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    const text = `${minutes}:${seconds}`;
    if (target.textContent !== text) target.textContent = text;
  }
}

if (!customElements.get('cart-reserve-timer')) {
  customElements.define('cart-reserve-timer', CartReserveTimer);
}
